import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { verifyEmail, resendVerification } from '../api/auth.api.js';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function VerifyEmail() {
  const query = useQuery();
  const token = (query.get('token') || '').trim();

  const [status, setStatus] = useState(token ? 'loading' : 'missing');
  const [message, setMessage] = useState('');

  // Resend form state
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!token) {
        setStatus('missing');
        setMessage('Aucun token de vérification fourni.');
        return;
      }
      try {
        await verifyEmail(token);
        if (!alive) return;
        setStatus('success');
        setMessage('Votre email a été vérifié avec succès. Vous pouvez maintenant vous connecter.');
      } catch (err) {
        if (!alive) return;
        setStatus('error');
        setMessage(err?.message || 'Le lien est invalide ou expiré.');
      }
    }

    run();
    return () => { alive = false; };
  }, [token]);

  async function handleResend(e) {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendStatus('sending');
    try {
      await resendVerification(resendEmail.trim());
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
        <h1 className="text-xl font-semibold text-slate-900 mb-4">Vérification Email</h1>

        {status === 'loading' && (
          <p className="text-slate-600">Vérification en cours...</p>
        )}
        {status === 'success' && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700">
            {message}
          </div>
        )}
        {status === 'missing' && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700">
            {message || 'Lien de vérification manquant.'}
          </div>
        )}
        {status === 'error' && (
          <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700">
            {message}
          </div>
        )}

        <div className="mt-6">
          <Link
            to="/auth"
            className="inline-flex items-center justify-center rounded-lg bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800"
          >
            Aller à la connexion
          </Link>
        </div>

        {/* Resend section */}
        {(status === 'error' || status === 'missing') && (
          <div className="mt-6 border-t border-slate-100 pt-5">
            <p className="text-sm text-slate-600 mb-3">
              Vous n'avez pas reçu le lien ou il a expiré ? Renvoyez-le.
            </p>
            {resendStatus === 'sent' ? (
              <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                Email de vérification renvoyé. Vérifiez votre boite mail.
              </p>
            ) : (
              <form onSubmit={handleResend} className="flex gap-2">
                <input
                  type="email"
                  placeholder="Votre email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  required
                />
                <button
                  type="submit"
                  disabled={resendStatus === 'sending'}
                  className="rounded-lg bg-slate-700 text-white px-3 py-2 text-sm hover:bg-slate-600 disabled:opacity-60"
                >
                  {resendStatus === 'sending' ? '...' : 'Renvoyer'}
                </button>
              </form>
            )}
            {resendStatus === 'error' && (
              <p className="mt-1 text-xs text-rose-600">Erreur lors de l'envoi. Réessayez.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
